/**
 * Visual cover (phase 4A) helpers.
 * Local time only. No AI. No clinical scoring changes.
 */

export const AURORA_DASHBOARD_APPEARANCE_KEY = "aurora_dashboard_visual_v1_appearance";

export const PERIOD_IDS = ["amanecer", "dia", "atardecer", "noche"];

export const OVERLAY_LEVELS = ["light", "medium", "dark"];

export const VISUAL_MODES = ["auto", "fixed", "none"];

const PUBLIC_BG = "/assets/dashboard-backgrounds";

export const DASHBOARD_BACKGROUNDS = [
  { id: "amanecer-01", period: "amanecer", src: `${PUBLIC_BG}/amanecer/aurora-amanecer-01.webp`, label: "Amanecer 1" },
  { id: "amanecer-02", period: "amanecer", src: `${PUBLIC_BG}/amanecer/aurora-amanecer-02.webp`, label: "Amanecer 2" },
  { id: "amanecer-03", period: "amanecer", src: `${PUBLIC_BG}/amanecer/aurora-amanecer-03.webp`, label: "Amanecer 3" },
  { id: "dia-01", period: "dia", src: `${PUBLIC_BG}/dia/aurora-dia-01.webp`, label: "Día 1" },
  { id: "dia-02", period: "dia", src: `${PUBLIC_BG}/dia/aurora-dia-02.webp`, label: "Día 2" },
  { id: "dia-03", period: "dia", src: `${PUBLIC_BG}/dia/aurora-dia-03.webp`, label: "Día 3" },
  { id: "atardecer-01", period: "atardecer", src: `${PUBLIC_BG}/atardecer/aurora-atardecer-01.webp`, label: "Atardecer 1" },
  { id: "atardecer-02", period: "atardecer", src: `${PUBLIC_BG}/atardecer/aurora-atardecer-02.webp`, label: "Atardecer 2" },
  { id: "atardecer-03", period: "atardecer", src: `${PUBLIC_BG}/atardecer/aurora-atardecer-03.webp`, label: "Atardecer 3" },
  { id: "noche-01", period: "noche", src: `${PUBLIC_BG}/noche/aurora-noche-01.webp`, label: "Noche 1" },
  { id: "noche-02", period: "noche", src: `${PUBLIC_BG}/noche/aurora-noche-02.webp`, label: "Noche 2" },
  { id: "noche-03", period: "noche", src: `${PUBLIC_BG}/noche/aurora-noche-03.webp`, label: "Noche 3" },
];

export const DEFAULT_APPEARANCE = {
  mode: "auto",
  backgroundId: "amanecer-01",
  overlay: "medium",
};

export function minutesOfDay(date = new Date()) {
  return date.getHours() * 60 + date.getMinutes();
}

/** Local bands: amanecer 05:00–08:59, día 09:00–16:59, atardecer 17:00–19:29, noche 19:30–04:59 */
export function resolveDayPeriod(date = new Date()) {
  const minutes = minutesOfDay(date);
  if (minutes >= 5 * 60 && minutes <= 8 * 60 + 59) return "amanecer";
  if (minutes >= 9 * 60 && minutes <= 16 * 60 + 59) return "dia";
  if (minutes >= 17 * 60 && minutes <= 19 * 60 + 29) return "atardecer";
  return "noche";
}

export function dayOfYearIndex(date = new Date()) {
  const year = date.getFullYear();
  const utcDay = Date.UTC(year, date.getMonth(), date.getDate());
  const utcStart = Date.UTC(year, 0, 1);
  return Math.floor((utcDay - utcStart) / 86400000);
}

export function rotationIndex(date = new Date()) {
  return dayOfYearIndex(date) % 3;
}

export function backgroundsForPeriod(period) {
  return DASHBOARD_BACKGROUNDS.filter((item) => item.period === period);
}

export function autoBackground(date = new Date()) {
  const period = resolveDayPeriod(date);
  const options = backgroundsForPeriod(period);
  return options[rotationIndex(date)] || options[0];
}

export function findBackgroundById(id) {
  return DASHBOARD_BACKGROUNDS.find((item) => item.id === id) || null;
}

export function overlayClassName(level) {
  if (level === "light") return "from-slate-950/25 via-slate-950/35 to-slate-950/70";
  if (level === "dark") return "from-slate-950/55 via-slate-950/65 to-slate-950/88";
  return "from-slate-950/40 via-slate-950/50 to-slate-950/80";
}

export function timeOfDayGreeting(date = new Date()) {
  const hour = date.getHours();
  if (hour >= 5 && hour < 12) return "Buenos días";
  if (hour >= 12 && hour < 19) return "Buenas tardes";
  return "Buenas noches";
}

export function professionalDisplayName(user, profile) {
  const titulo = String(user?.titulo || user?.tratamiento || profile?.titulo || "").trim();
  const raw = String(profile?.nombre_completo || user?.nombre || "").trim();
  if (!raw) return "";
  if (/^((dr|dra)\.?|doctor|doctora)\b/i.test(raw)) return raw.replace(/\s+/g, " ");
  if (titulo) return `${titulo} ${raw}`.replace(/\s+/g, " ").trim();
  return raw.replace(/\s+/g, " ");
}

/** One greeting line. Neutral if the profile has no name. Never invents a fixed clinician. */
export function personalizedGreeting(date, user, profile) {
  const hello = timeOfDayGreeting(date);
  const name = professionalDisplayName(user, profile);
  return name ? `${hello}, ${name}` : hello;
}

export function journeyMessage(period) {
  const messages = {
    amanecer: "Un inicio sereno para su jornada clínica.",
    dia: "Aurora le acompaña con calma en la consulta de hoy.",
    atardecer: "Cierre de jornada con claridad y sosiego.",
    noche: "Revisión nocturna con serenidad.",
  };
  return messages[period] || messages.dia;
}

export function resolveLocale(explicit) {
  if (explicit) return explicit;
  if (typeof navigator !== "undefined" && navigator.language) return navigator.language;
  return "es-419";
}

export function formatLocalizedDate(date = new Date(), locale) {
  const resolved = resolveLocale(locale);
  const label = new Intl.DateTimeFormat(resolved, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : label;
}

export function loadAppearance() {
  try {
    const raw = localStorage.getItem(AURORA_DASHBOARD_APPEARANCE_KEY);
    if (!raw) return { ...DEFAULT_APPEARANCE };
    const parsed = JSON.parse(raw);
    const mode = VISUAL_MODES.includes(parsed?.mode) ? parsed.mode : "auto";
    const overlay = OVERLAY_LEVELS.includes(parsed?.overlay) ? parsed.overlay : "medium";
    const backgroundId = findBackgroundById(parsed?.backgroundId)?.id || DEFAULT_APPEARANCE.backgroundId;
    return { mode, overlay, backgroundId };
  } catch {
    return { ...DEFAULT_APPEARANCE };
  }
}

export function saveAppearance(next) {
  const current = loadAppearance();
  const merged = {
    mode: VISUAL_MODES.includes(next?.mode) ? next.mode : current.mode,
    overlay: OVERLAY_LEVELS.includes(next?.overlay) ? next.overlay : current.overlay,
    backgroundId: findBackgroundById(next?.backgroundId)?.id || current.backgroundId,
  };
  localStorage.setItem(AURORA_DASHBOARD_APPEARANCE_KEY, JSON.stringify(merged));
  return merged;
}

export function resolveHeroVisual(appearance, date = new Date()) {
  const prefs = appearance || DEFAULT_APPEARANCE;
  const period = resolveDayPeriod(date);
  if (prefs.mode === "none") {
    return {
      mode: "none",
      period,
      background: null,
      src: null,
      overlay: prefs.overlay,
    };
  }
  if (prefs.mode === "fixed") {
    const background = findBackgroundById(prefs.backgroundId) || autoBackground(date);
    return {
      mode: "fixed",
      period,
      background,
      src: background.src,
      overlay: prefs.overlay,
    };
  }
  const background = autoBackground(date);
  return {
    mode: "auto",
    period,
    background,
    src: background.src,
    overlay: prefs.overlay,
  };
}
